// What makes a deck, and which deck each seat is dealt. Pure, so the host's dealing and
// the lobby's deckbuilder answer from the same rules — and because a player's own deck
// arrives inside synced state, where any device that knows the code can POST anything.

export const DECK_SIZE = 30;
export const MAX_COPIES = 2;
export const OPENING_HAND_SIZE = 4;

// The eighteen cards every deck was built from before decks could be built. Guests,
// bots and "Samma för alla" games still get exactly this mix: the first twelve twice,
// the last six once. Cards added since are reached only through deckbuilding, so this
// list is frozen rather than read off the library.
export const STANDARD_CARD_IDS = Object.freeze([
  "zero-lager",
  "lime-mocktail",
  "bubbelvatten",
  "virgin-colada",
  "gurkvatten",
  "espresso-tonic",
  "jordgubbs-fizz",
  "isvatten",
  "ingefara",
  "kombucha",
  "citronpress",
  "sista-droppen",
  "barhandduk",
  "spegelglas",
  "kapsyl",
  "kaktus",
  "happyhour",
  "partytalt",
]);

// The 2 mot 2 cards. They need a partner, so a table with sides deals them on top of the
// eighteen and no other standard deck ever does. Frozen like the eighteen: the teamwork
// cards added since are reached through the builder's Lag tab, and from there they come
// along to every mode, solo half and all.
export const TEAM_CARD_IDS = Object.freeze(["lagskal", "ryggstod", "passning", "dubbelstot", "uppoffring"]);

export function standardDeck({ teams = false } = {}) {
  const pool = teams ? [...STANDARD_CARD_IDS, ...TEAM_CARD_IDS] : STANDARD_CARD_IDS;
  return Array.from({ length: DECK_SIZE }, (_, index) => pool[index % pool.length]);
}

export function countCards(cards) {
  const tally = new Map();
  cards.forEach((id) => tally.set(id, (tally.get(id) || 0) + 1));
  return tally;
}

export function deckProblems(cards, knownIds) {
  const known = new Set(knownIds);
  const tally = countCards(cards);
  return {
    total: cards.length,
    unknown: [...tally.keys()].filter((id) => !known.has(id)),
    overLimit: [...tally].filter(([, count]) => count > MAX_COPIES).map(([id]) => id),
  };
}

export function isValidDeck(cards, knownIds) {
  if (!Array.isArray(cards)) return false;
  const { total, unknown, overLimit } = deckProblems(cards, knownIds);
  return total === DECK_SIZE && unknown.length === 0 && overLimit.length === 0;
}

// The picks a deck can actually supply: at most four, and never more copies of a card
// than the deck holds.
export function fitOpeningHand(picks, cards) {
  const left = countCards(cards);
  const hand = [];
  for (const id of Array.isArray(picks) ? picks : []) {
    if (hand.length >= OPENING_HAND_SIZE) break;
    if (!left.get(id)) continue;
    left.set(id, left.get(id) - 1);
    hand.push(id);
  }
  return hand;
}

// The deck minus one copy per removed card.
export function withoutCards(cards, removed) {
  const pending = countCards(removed);
  return cards.filter((id) => {
    if (!pending.get(id)) return true;
    pending.set(id, pending.get(id) - 1);
    return false;
  });
}

// The host's two switches, allow-listed: they arrive in synced state like everything else.
export function normalizeSettings(raw) {
  return {
    deckMode: raw?.deckMode === "shared" ? "shared" : "own",
    openingHand: raw?.openingHand === "fixed" ? "fixed" : "random",
  };
}

// What one seat is dealt. An own deck is used only if it is legal; anything else — a
// guest, "Samma för alla", a forged list — gets the standard deck, with the teamwork
// cards in it when the table has sides. With a fixed opening hand the picks go straight
// to hand and the rest of the deck is shuffled under them.
export function startingCards({ player, settings, knownIds, shuffle, teams = false }) {
  const rules = normalizeSettings(settings);
  if (rules.deckMode === "shared" || !isValidDeck(player.customDeck, knownIds)) {
    return { deck: shuffle(standardDeck({ teams })), openingHand: [] };
  }
  const openingHand = rules.openingHand === "fixed" ? fitOpeningHand(player.openingHand, player.customDeck) : [];
  return { deck: shuffle(withoutCards(player.customDeck, openingHand)), openingHand };
}
